import * as THREE from 'three';

export interface EnvironmentPreset {
  name: string;
  skyTopColor: number;
  skyBottomColor: number;
  sunColor: number;
  sunIntensity: number;
  sunPosition: THREE.Vector3;
  ambientColor: number;
  ambientIntensity: number;
  fogColor: number;
  fogDensity: number;
  starOpacity: number;
  emissiveFactor: number; // 0 (day) to 1.0 (night)
  exposure: number;
}

export const TIME_PRESETS: EnvironmentPreset[] = [
  // 0: Dawn (Alpenglow)
  {
    name: 'dawn',
    skyTopColor: 0x2b3863,
    skyBottomColor: 0xf89b74,
    sunColor: 0xffaa66,
    sunIntensity: 1.4,
    sunPosition: new THREE.Vector3(70, 18, 70),
    ambientColor: 0x5a6288,
    ambientIntensity: 0.8,
    fogColor: 0xe09b82,
    fogDensity: 0.0035,
    starOpacity: 0.2,
    emissiveFactor: 0.4,
    exposure: 0.95,
  },
  // 1: Morning
  {
    name: 'morning',
    skyTopColor: 0x38bdf8,
    skyBottomColor: 0xcfeafe,
    sunColor: 0xfff3db,
    sunIntensity: 2.0,
    sunPosition: new THREE.Vector3(80, 75, 45),
    ambientColor: 0x8ba6c9,
    ambientIntensity: 1.0,
    fogColor: 0xc4e2fa,
    fogDensity: 0.002,
    starOpacity: 0.0,
    emissiveFactor: 0.0,
    exposure: 1.05,
  },
  // 2: Noon
  {
    name: 'noon',
    skyTopColor: 0x0284c7,
    skyBottomColor: 0xbae6fd,
    sunColor: 0xffffff,
    sunIntensity: 2.4,
    sunPosition: new THREE.Vector3(20, 100, 20),
    ambientColor: 0x93b5d9,
    ambientIntensity: 1.1,
    fogColor: 0xb5ddf5,
    fogDensity: 0.0018,
    starOpacity: 0.0,
    emissiveFactor: 0.0,
    exposure: 1.1,
  },
  // 3: Golden Hour
  {
    name: 'golden',
    skyTopColor: 0x3b4d82,
    skyBottomColor: 0xfb923c,
    sunColor: 0xffa338,
    sunIntensity: 2.2,
    sunPosition: new THREE.Vector3(-80, 28, -50),
    ambientColor: 0x7a6374,
    ambientIntensity: 0.9,
    fogColor: 0xeaa778,
    fogDensity: 0.003,
    starOpacity: 0.05,
    emissiveFactor: 0.3,
    exposure: 1.0,
  },
  // 4: Dusk
  {
    name: 'dusk',
    skyTopColor: 0x1e1e40,
    skyBottomColor: 0xa855f7,
    sunColor: 0xf43f5e,
    sunIntensity: 1.0,
    sunPosition: new THREE.Vector3(-90, 8, -60),
    ambientColor: 0x393259,
    ambientIntensity: 0.6,
    fogColor: 0x4f3669,
    fogDensity: 0.004,
    starOpacity: 0.5,
    emissiveFactor: 0.8,
    exposure: 0.88,
  },
  // 5: Night
  {
    name: 'night',
    skyTopColor: 0x050814,
    skyBottomColor: 0x0d1933,
    sunColor: 0x93c5fd, // moonlight
    sunIntensity: 0.7,
    sunPosition: new THREE.Vector3(40, 80, -60),
    ambientColor: 0x162238,
    ambientIntensity: 0.45,
    fogColor: 0x0a1426,
    fogDensity: 0.0045,
    starOpacity: 1.0,
    emissiveFactor: 1.0,
    exposure: 0.8,
  },
];

export class EnvironmentSystem {
  public root: THREE.Group = new THREE.Group();
  public sunLight: THREE.DirectionalLight;
  public ambientLight: THREE.AmbientLight;
  public skyMesh: THREE.Mesh;
  public starsPoints: THREE.Points;
  public fog: THREE.FogExp2;

  private skyShaderMat!: THREE.ShaderMaterial;
  private currentPresetIndex: number = 1;
  private targetPresetIndex: number = 1;
  private transitionAlpha: number = 1.0;
  private transitionDuration: number = 1.2;
  private currentEmissive: number = 0;

  constructor() {
    this.root.name = 'EnvironmentSystem';

    // Lights
    this.ambientLight = new THREE.AmbientLight(0x8ba6c9, 1.0);
    this.root.add(this.ambientLight);

    this.sunLight = new THREE.DirectionalLight(0xfff3db, 2.0);
    this.sunLight.position.set(80, 75, 45);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.width = 1024;
    this.sunLight.shadow.mapSize.height = 1024;
    this.sunLight.shadow.camera.near = 5;
    this.sunLight.shadow.camera.far = 300;
    const d = 60;
    this.sunLight.shadow.camera.left = -d;
    this.sunLight.shadow.camera.right = d;
    this.sunLight.shadow.camera.top = d;
    this.sunLight.shadow.camera.bottom = -d;
    this.sunLight.shadow.bias = -0.0005;
    this.root.add(this.sunLight);

    // Fog
    this.fog = new THREE.FogExp2(0xc4e2fa, 0.002);

    // Stylized procedural Sky Dome
    this.skyMesh = this.createSkyDome();
    this.root.add(this.skyMesh);

    // Starfield for night
    this.starsPoints = this.createStarfield();
    this.root.add(this.starsPoints);

    this.applyPreset(1, true);
  }

  private createSkyDome(): THREE.Mesh {
    const geo = new THREE.SphereGeometry(450, 32, 16);
    this.skyShaderMat = new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: new THREE.Color(0x38bdf8) },
        bottomColor: { value: new THREE.Color(0xcfeafe) },
        offset: { value: 20 },
        exponent: { value: 0.6 },
      },
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPosition = worldPosition.xyz;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform float offset;
        uniform float exponent;
        varying vec3 vWorldPosition;
        void main() {
          float h = normalize(vWorldPosition + offset).y;
          gl_FragColor = vec4(mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0)), 1.0);
        }
      `,
      side: THREE.BackSide,
      depthWrite: false,
    });

    return new THREE.Mesh(geo, this.skyShaderMat);
  }

  private createStarfield(): THREE.Points {
    const count = 600;
    const positions = new Float32Array(count * 3);
    const radius = 430;

    for (let i = 0; i < count; i++) {
      const u = Math.random();
      const v = Math.random();
      const theta = u * 2.0 * Math.PI;
      const phi = Math.acos(2.0 * v - 1.0);
      // Keep stars mostly in upper hemisphere
      const y = Math.abs(radius * Math.cos(phi)) + 20;
      const x = radius * Math.sin(phi) * Math.cos(theta);
      const z = radius * Math.sin(phi) * Math.sin(theta);

      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    const mat = new THREE.PointsMaterial({
      color: 0xffffff,
      size: 2.2,
      sizeAttenuation: false,
      transparent: true,
      opacity: 0.0,
      depthWrite: false,
    });

    return new THREE.Points(geo, mat);
  }

  public setTimePreset(index: number, immediate: boolean = false): void {
    const bounded = ((index % 6) + 6) % 6;
    if (immediate) {
      this.currentPresetIndex = bounded;
      this.targetPresetIndex = bounded;
      this.transitionAlpha = 1.0;
      this.applyPreset(bounded, true);
    } else {
      this.currentPresetIndex = this.targetPresetIndex;
      this.targetPresetIndex = bounded;
      this.transitionAlpha = 0.0;
    }
  }

  public update(dt: number): void {
    if (this.transitionAlpha < 1.0) {
      this.transitionAlpha = Math.min(1.0, this.transitionAlpha + dt / this.transitionDuration);
      this.blendPresets(this.currentPresetIndex, this.targetPresetIndex, this.transitionAlpha);
    }
  }

  private applyPreset(index: number, immediate: boolean): void {
    const p = TIME_PRESETS[index];
    this.blendPresets(index, index, 1.0);
    if (immediate) {
      this.sunLight.position.copy(p.sunPosition);
    }
  }

  private blendPresets(iA: number, iB: number, t: number): void {
    const pA = TIME_PRESETS[iA];
    const pB = TIME_PRESETS[iB];

    // Lerp colors
    const topCol = new THREE.Color(pA.skyTopColor).lerp(new THREE.Color(pB.skyTopColor), t);
    const btmCol = new THREE.Color(pA.skyBottomColor).lerp(new THREE.Color(pB.skyBottomColor), t);
    const sunCol = new THREE.Color(pA.sunColor).lerp(new THREE.Color(pB.sunColor), t);
    const ambCol = new THREE.Color(pA.ambientColor).lerp(new THREE.Color(pB.ambientColor), t);
    const fogCol = new THREE.Color(pA.fogColor).lerp(new THREE.Color(pB.fogColor), t);

    this.skyShaderMat.uniforms.topColor.value.copy(topCol);
    this.skyShaderMat.uniforms.bottomColor.value.copy(btmCol);

    this.sunLight.color.copy(sunCol);
    this.sunLight.intensity = THREE.MathUtils.lerp(pA.sunIntensity, pB.sunIntensity, t);
    this.sunLight.position.lerpVectors(pA.sunPosition, pB.sunPosition, t);

    this.ambientLight.color.copy(ambCol);
    this.ambientLight.intensity = THREE.MathUtils.lerp(pA.ambientIntensity, pB.ambientIntensity, t);

    this.fog.color.copy(fogCol);
    this.fog.density = THREE.MathUtils.lerp(pA.fogDensity, pB.fogDensity, t);

    const starMat = this.starsPoints.material as THREE.PointsMaterial;
    starMat.opacity = THREE.MathUtils.lerp(pA.starOpacity, pB.starOpacity, t);

    this.currentEmissive = THREE.MathUtils.lerp(pA.emissiveFactor, pB.emissiveFactor, t);
  }

  public getEmissiveFactor(): number {
    return this.currentEmissive;
  }
}
